import { useEffect, useState } from "react";
import { frDate, previewAction, previewTitle, toIso, todayInput } from "../lib/preview";
import { partyColor, partyName } from "../lib/palette";
import type { SignField, SignParty } from "../types";
import { IcoChevron, IcoChevronLeft, IcoChevronRight, IcoClose } from "../ui/icons";
import { SignPad } from "../ui/SignPad";

export function PreviewDock({
  fields,
  parties,
  step,
  value,
  miss,
  pageWidth,
  onValue,
  onPrev,
  onNext,
  onClose,
  lastAction,
}: {
  fields: SignField[];
  parties: SignParty[];
  step: number;
  value: string;
  miss: boolean;
  pageWidth: number;
  onValue: (v: string) => void;
  onPrev: () => void;
  onNext: () => void;
  onClose?: () => void;
  lastAction?: string;
}) {
  const field = fields[step];
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(false);
  }, [step]);
  if (!field) return null;
  const last = step === fields.length - 1;
  const color = partyColor(parties, field.party_id);
  const kind = field.kind;
  const options = field.options && field.options.length ? field.options : ["Oui", "Non"];

  return (
    <div className="sg-dock" style={pageWidth > 0 ? { width: pageWidth } : undefined}>
      <div className="sg-dock__top">
        <strong style={{ color }}>{previewTitle(field)}</strong>
        <span>
          {step + 1} / {fields.length} · {partyName(parties, field.party_id)}
        </span>
        {onClose ? (
          <button type="button" className="sg-btn ghost" onClick={onClose}>
            <IcoClose />
            Quitter
          </button>
        ) : null}
      </div>
      <form
        className={`sg-dock__form${miss ? " is-miss" : ""}${kind === "signature" ? " is-sign" : ""}`}
        onSubmit={(e) => {
          e.preventDefault();
          onNext();
        }}
      >
        <button type="button" className="sg-btn ghost" disabled={step < 1} onClick={onPrev}>
          <IcoChevronLeft />
          Retour
        </button>
        {kind === "signature" ? (
          <SignPad key={step} value={value} color="#2a1b36" onChange={onValue} />
        ) : kind === "select" ? (
          <div className="sg-docksel">
            <button type="button" className="sg-docksel__btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
              {value || "Choisir…"}
            </button>
            {open ? (
              <ul>
                {options.map((opt) => (
                  <li key={opt}>
                    <button
                      type="button"
                      className={opt === value ? "is-on" : ""}
                      onClick={() => {
                        onValue(opt);
                        setOpen(false);
                      }}
                    >
                      {opt}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : kind === "date" ? (
          <input
            type="date"
            value={toIso(value) || todayInput()}
            onChange={(e) => onValue(frDate(e.target.value))}
            aria-label={previewTitle(field)}
          />
        ) : kind === "number" ? (
          <div className="sg-docknum">
            <input
              inputMode="decimal"
              value={value}
              placeholder="0"
              aria-label={previewTitle(field)}
              onChange={(e) => onValue(e.target.value.replace(/[^\d,.\-]/g, ""))}
            />
            <span className="sg-docknum__spin">
              <button
                type="button"
                className="is-up"
                aria-label="Augmenter"
                onClick={() => {
                  const n = Number(String(value).replace(",", "."));
                  onValue(String((Number.isFinite(n) ? n : 0) + 1));
                }}
              >
                <IcoChevron />
              </button>
              <button
                type="button"
                aria-label="Diminuer"
                onClick={() => {
                  const n = Number(String(value).replace(",", "."));
                  onValue(String((Number.isFinite(n) ? n : 0) - 1));
                }}
              >
                <IcoChevron />
              </button>
            </span>
          </div>
        ) : kind === "phone" ? (
          <input type="tel" value={value} placeholder="06 12 34 56 78" onChange={(e) => onValue(e.target.value)} />
        ) : kind === "check" || kind === "radio" ? (
          <p className="sg-dock__hint">{value ? "Coché — Suivant pour continuer." : "Cochez, puis continuez."}</p>
        ) : kind === "paraphe" ? (
          <input
            value={value}
            autoFocus
            maxLength={2}
            autoComplete="off"
            spellCheck={false}
            placeholder="AB"
            aria-label="Deux lettres"
            onChange={(e) => onValue(e.target.value.replace(/[^A-Za-zÀ-ÿ]/g, "").slice(0, 2).toUpperCase())}
          />
        ) : (
          <input
            value={value}
            autoFocus
            placeholder={previewTitle(field)}
            onChange={(e) => onValue(e.target.value)}
          />
        )}
        <button type="submit" className="sg-btn">
          {last && lastAction ? lastAction : previewAction(kind, last)}
          <IcoChevronRight />
        </button>
      </form>
    </div>
  );
}
